import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DocumentVariablesService from '../services/documentVariablesService';
import { DocumentVariable, VariableValue } from '../types/documentVariables';
import toast from 'react-hot-toast';

interface UseDocumentVariablesOptions {
  templateId?: string;
  customerId?: string;
  documentId?: string;
  autoLoad?: boolean;
}

export const useDocumentVariables = (options: UseDocumentVariablesOptions = {}) => {
  const { user } = useAuth();
  const [variables, setVariables] = useState<DocumentVariable[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const organizationId = user?.organizationId;

  // Változók betöltése
  const loadVariables = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);

    try {
      let loadedVariables: DocumentVariable[];
      
      if (options.templateId) {
        loadedVariables = await DocumentVariablesService.getTemplateVariables(
          organizationId, 
          options.templateId
        );
      } else {
        loadedVariables = await DocumentVariablesService.getOrganizationVariables(organizationId);
      }

      setVariables(loadedVariables);

      // Értékek betöltése ha van kontextus
      if (options.templateId || options.customerId || options.documentId) {
        const loadedValues = await DocumentVariablesService.getVariableValues(
          organizationId,
          {
            templateId: options.templateId,
            customerId: options.customerId,
            documentId: options.documentId
          }
        );
        setValues(loadedValues);
      }
    } catch (err) {
      console.error('Error loading variables:', err);
      setError('Hiba a változók betöltése során');
      toast.error('Hiba a változók betöltése során');
    } finally {
      setLoading(false);
    }
  }, [organizationId, options.templateId, options.customerId, options.documentId]);

  // Változó érték frissítése (lokálisan)
  const updateValue = useCallback((variableId: string, value: string) => {
    setValues(prev => ({
      ...prev,
      [variableId]: value
    }));
  }, []);

  // Változó érték frissítése kulcs alapján
  const updateValueByKey = useCallback((key: string, value: string) => {
    const variable = variables.find(v => v.key === key);
    if (variable) {
      updateValue(variable.id, value);
    }
  }, [variables, updateValue]);

  // Értékek mentése
  const saveValues = useCallback(async (valuesToSave?: Record<string, string>) => {
    if (!organizationId) return;

    setSaving(true);
    setError(null);

    try {
      await DocumentVariablesService.saveVariableValues(
        organizationId,
        valuesToSave || values,
        {
          templateId: options.templateId,
          customerId: options.customerId,
          documentId: options.documentId
        }
      );

      toast.success('Változók mentve');
    } catch (err) {
      console.error('Error saving values:', err);
      setError('Hiba a mentés során');
      toast.error('Hiba a mentés során');
    } finally {
      setSaving(false);
    }
  }, [organizationId, values, options]);

  // Új változó létrehozása
  const createVariable = useCallback(async (
    variable: Omit<DocumentVariable, 'id' | 'organizationId' | 'createdAt' | 'updatedAt' | 'createdBy'>
  ) => {
    if (!organizationId || !user?.uid) return;

    setLoading(true);
    setError(null);

    try {
      const variableId = await DocumentVariablesService.createVariable(
        {
          ...variable,
          organizationId,
          createdBy: user.uid
        },
        user.uid
      );

      // Frissítjük a listát
      await loadVariables();
      
      toast.success('Változó létrehozva');
      return variableId;
    } catch (err) {
      console.error('Error creating variable:', err);
      setError('Hiba a változó létrehozása során');
      toast.error('Hiba a változó létrehozása során');
    } finally {
      setLoading(false);
    }
  }, [organizationId, user?.uid, loadVariables]);

  // Változó frissítése
  const updateVariable = useCallback(async (
    variableId: string,
    updates: Partial<DocumentVariable>
  ) => {
    setLoading(true);
    setError(null);

    try {
      await DocumentVariablesService.updateVariable(variableId, updates);
      
      // Lokális frissítés
      setVariables(prev => prev.map(v => 
        v.id === variableId ? { ...v, ...updates } : v
      ));
      
      toast.success('Változó frissítve');
    } catch (err) {
      console.error('Error updating variable:', err);
      setError('Hiba a változó frissítése során');
      toast.error('Hiba a változó frissítése során');
    } finally {
      setLoading(false);
    }
  }, []);

  // Változó törlése
  const deleteVariable = useCallback(async (variableId: string) => {
    setLoading(true);
    setError(null);

    try {
      await DocumentVariablesService.deleteVariable(variableId);
      
      // Lokális eltávolítás
      setVariables(prev => prev.filter(v => v.id !== variableId));
      
      toast.success('Változó törölve');
    } catch (err) {
      console.error('Error deleting variable:', err);
      setError('Hiba a változó törlése során');
      toast.error('Hiba a változó törlése során');
    } finally {
      setLoading(false);
    }
  }, []);

  // Dokumentum adatok előkészítése
  const prepareDocumentData = useCallback(async (
    templateId: string,
    customerId?: string,
    additionalValues: Record<string, string> = {}
  ) => {
    if (!organizationId) return {};

    try {
      return await DocumentVariablesService.prepareDocumentData(
        organizationId,
        templateId,
        customerId,
        additionalValues
      );
    } catch (err) {
      console.error('Error preparing document data:', err);
      toast.error('Hiba a dokumentum adatok előkészítése során');
      return {};
    }
  }, [organizationId]);

  // Validáció
  const validateValues = useCallback(async (
    templateId: string,
    valuesToValidate?: Record<string, string>
  ) => {
    if (!organizationId) return { isValid: false, errors: {} };

    try {
      // Értékek konvertálása kulcs alapúra
      const keyBasedValues: Record<string, string> = {};
      const currentValues = valuesToValidate || values;
      
      variables.forEach(variable => {
        if (currentValues[variable.id]) {
          keyBasedValues[variable.key] = currentValues[variable.id];
        }
      });

      return await DocumentVariablesService.validateDocumentData(
        organizationId,
        templateId,
        keyBasedValues
      );
    } catch (err) {
      console.error('Error validating values:', err);
      return { isValid: false, errors: {} };
    }
  }, [organizationId, values, variables]);

  // Alapértelmezett változók inicializálása
  const initializeDefaults = useCallback(async () => {
    if (!organizationId || !user?.uid) return;

    setLoading(true);
    try {
      await DocumentVariablesService.initializeDefaultVariables(organizationId, user.uid);
      await loadVariables();
      toast.success('Alapértelmezett változók létrehozva');
    } catch (err) {
      console.error('Error initializing defaults:', err);
      toast.error('Hiba az alapértelmezett változók létrehozása során');
    } finally {
      setLoading(false);
    }
  }, [organizationId, user?.uid, loadVariables]);

  // Sablon változók automatikus felismerése
  const extractVariablesFromTemplate = useCallback((templateContent: string) => {
    return DocumentVariablesService.extractVariablesFromTemplate(templateContent);
  }, []);

  // Hiányzó változók létrehozása
  const createMissingVariables = useCallback(async (
    templateId: string,
    variableKeys: string[]
  ) => {
    if (!organizationId || !user?.uid) return;

    try {
      await DocumentVariablesService.createMissingVariables(
        organizationId,
        templateId,
        variableKeys,
        user.uid
      );
      await loadVariables();
      toast.success(`${variableKeys.length} új változó létrehozva`);
    } catch (err) {
      console.error('Error creating missing variables:', err);
      toast.error('Hiba a hiányzó változók létrehozása során');
    }
  }, [organizationId, user?.uid, loadVariables]);

  // Változók kategóriák szerint csoportosítva
  const variablesByCategory = variables.reduce((acc, variable) => {
    const category = variable.category || 'custom';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(variable);
    return acc;
  }, {} as Record<string, DocumentVariable[]>);

  // Kötelező változók ellenőrzése
  const requiredVariables = variables.filter(v => v.required);
  const missingRequiredValues = requiredVariables.filter(v => !values[v.id]);

  // Automatikus betöltés
  useEffect(() => {
    if (options.autoLoad !== false && organizationId) {
      loadVariables();
    }
  }, [loadVariables, organizationId, options.autoLoad]);

  return {
    // Adatok
    variables,
    values,
    variablesByCategory,
    requiredVariables,
    missingRequiredValues,
    
    // Állapotok
    loading,
    saving,
    error,
    
    // Műveletek
    loadVariables,
    updateValue,
    updateValueByKey,
    saveValues,
    createVariable,
    updateVariable,
    deleteVariable,
    prepareDocumentData,
    validateValues,
    initializeDefaults,
    extractVariablesFromTemplate,
    createMissingVariables
  };
};

export default useDocumentVariables;
